import type { DetectedObjectResults } from '@brightmls/nextgen.lae.mediacenter';

const STORAGE_PREFIX = 'objectDetection_v2_';
const EVICTION_DAYS = 90;

interface PersistedData {
  results: DetectedObjectResults;
  processedPhotoKeys: string[];
  _timestamp: number;
}

export function loadDetectedObjects(listingKey: string): PersistedData | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + listingKey);
    if (!raw) return null;
    const data: PersistedData = JSON.parse(raw);
    // Validate shape
    if (!data.results || !data._timestamp) return null;
    // Check if stale
    const age = Date.now() - data._timestamp;
    if (age > EVICTION_DAYS * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(STORAGE_PREFIX + listingKey);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

export function persistDetectedObjects(
  listingKey: string,
  results: DetectedObjectResults,
  processedPhotoKeys: string[],
): void {
  try {
    const data: PersistedData = {
      results,
      processedPhotoKeys,
      _timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_PREFIX + listingKey, JSON.stringify(data));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

export function clearDetectedObjects(listingKey: string): void {
  try {
    localStorage.removeItem(STORAGE_PREFIX + listingKey);
  } catch {
    // ignore
  }
}

/**
 * Scan all objectDetection_v2_* keys, remove entries older than 90 days.
 * Called once on mount.
 */
export function evictStaleEntries(): void {
  try {
    const maxAge = EVICTION_DAYS * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(STORAGE_PREFIX)) continue;
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const data = JSON.parse(raw);
        if (data._timestamp && now - data._timestamp > maxAge) {
          keysToRemove.push(key);
        }
      } catch {
        // Corrupted entry — remove it
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
}
